import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import {
  answerBlock,
  getSpecialRateProvisions,
  clearSpecialRateProvisions,
  getOtherArticles,
} from 'actions';

import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import QuestionBlock from 'components/Questionnaires/QuestionBlock';
import Question from 'components/Questionnaires/Question';
import GetDictionaryArray from 'common/language/intlArrayHelper';
import {
  QuestionCheckBoxField,
  QuestionDropDownField,
  QuestionJurisdictionDropDownField,
  QuestionYesNoField
} from 'components/Questionnaires/fields';
import {
  QuestionBlockRepeater,
  QuestionTreatySpecialRatesClaimBlock
} from 'components/Questionnaires/blocks';
import {
  requiredValidator,
  validateQuestion,
  isBlockFullyValid } from 'components/Questionnaires/validators';
import showQuestion from 'components/Questionnaires/questions';

export const QuestionTaxTreatyClaimBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [answer, setAnswer] = useState(block?.answers ?? {});
  const { componentProps } = block;
  const { entityType } = componentProps || {};
  const otherArticlesByCountry = useSelector(state => state?.app?.otherArticles);
  const qAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);
  const lobStaticData = useSelector(state => state?.app?.staticData?.lobs);
  const lobArray = useMemo(() => GetDictionaryArray('questionnaire.taxTreatyClaim.limitationOnBenefit.items', intl), []);

  const getLobItems = (countryAnswer) => {
    let result = lobArray ?? [];
    const otherArticles = otherArticlesByCountry[countryAnswer] ?? [];
    if (!otherArticles || otherArticles.length === 0) {
      result = result.filter(item => item.value !== 'Other');
    }

    if (lobStaticData && lobStaticData.length > 0) {
      const countryLob = lobStaticData.find(item => item.countryCodeAlpha === countryAnswer);
      if (countryLob) {
        const valueMap = result.reduce((map, item) => {
          map[item.value] = item;
          return map;
        }, {});

        const newResult = [];
        newResult.push(valueMap['']);

        if (!countryLob.hasLobProvision) {
          newResult.push(valueMap.NoLOBArticleInTreaty);
        } else {
          if (countryLob.government) newResult.push(valueMap.Government);
          if (countryLob.taxExempt) newResult.push(valueMap.TaxExempt);
          if (countryLob.otherTaxExempt) newResult.push(valueMap.OtherTaxExempt);
          if (countryLob.publiclyTradedCorporation) newResult.push(valueMap.PublicTradedCorp);
          if (countryLob.subsidiary) newResult.push(valueMap.Subsidiary);
          if (countryLob.meetsOwnershipTest) newResult.push(valueMap.OwnershipAndBaseErosion);
          if (countryLob.derivativeBenefitsTest) newResult.push(valueMap.DerivativeBenefitsTest);
          if (countryLob.activeTradeBusinessTest) newResult.push(valueMap.ActiveTradeOrBusinessTest);
          if (countryLob.discretionaryDetermination) newResult.push(valueMap.FavourableDeterminationByUS);
          if (countryLob.others) newResult.push(valueMap.Other);
        }

        if (newResult.length > 0) {
          result = newResult;
        }
      }
    }

    result = result.map(item => Object.assign({ value: item.value, label: item.label }));

    return result;
  };

  const getOthers = (countryAnswer) => {
    if (!otherArticlesByCountry || !otherArticlesByCountry[countryAnswer]) {
      return [];
    }

    const otherArticles = otherArticlesByCountry[countryAnswer]
      .map(item => Object.assign({ value: item, label: item }));

    return [{ value: '', label: intl.formatMessage({ id: 'questionnaire.taxTreatyClaim.other.placeholder' }) }, ...otherArticles];
  };

  const isEmptyObject = (obj) => {
    if (!obj) {
      return true;
    }
    let isEmpty = true;
    Object.keys(obj).forEach((key) => {
      if (obj[key]) {
        isEmpty = false;
      }
    });
    return isEmpty;
  };

  const [claimed, setClaimed] = useState({
    id: 'claimed',
    label: entityType === 'Entity'
      ? 'questionnaire.treatyClaim.claimed.entity.label'
      : 'questionnaire.treatyClaim.claimed.individual.label',
    component: QuestionYesNoField,
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.treatyClaim.claimed.errorMessage' }
      ]
    },
    answer: answer?.claimed ?? undefined
  });
  const [country, setCountry] = useState({
    id: 'country',
    label: 'questionnaire.treatyClaim.country.label',
    info: {
      instructions: 'questionnaire.treatyClaim.country.info'
    },
    component: QuestionJurisdictionDropDownField,
    componentProps: {
      treatiesOnly: true
    },
    visible: bAnswers => bAnswers?.claimed === 'Yes',
    answer: answer?.country ?? undefined,
  });
  const [limitationOnBenefit, setLimitationOnBenefit] = useState({
    id: 'limitationOnBenefit',
    label: 'questionnaire.taxTreatyClaim.limitationOnBenefit.label',
    component: QuestionDropDownField,
    componentProps:
    {
      items: [],
      preventTranslation: true
    },
    visible: bAnswers => entityType === 'Entity' && bAnswers?.country,
    answer: answer?.limitationOnBenefit ?? undefined,
  });
  const [other, setOther] = useState({
    id: 'other',
    label: 'questionnaire.taxTreatyClaim.other.label',
    component: QuestionDropDownField,
    componentProps: {
      items: [],
      preventTranslation: true
    },
    visible: bAnswers => bAnswers?.limitationOnBenefit === 'Other',
    answer: answer?.other ?? undefined,
  });
  const [isUsSourceDividends, setIsUsSourceDividends] = useState({
    id: 'isUsSourceDividends',
    component: QuestionCheckBoxField,
    componentProps:
    {
      option: 'questionnaire.taxTreatyClaim.isUsSourceDividends.label'
    },
    visible: bAnswers => entityType === 'Entity' && bAnswers?.country,
    answer: answer?.isUsSourceDividends ?? undefined,
  });
  const [treatySpecialRatesClaims, setTreatySpecialRatesClaims] = useState({
    id: 'treatySpecialRatesClaims',
    component: QuestionBlockRepeater,
    componentProps: {
      collapsible: true
    },
    className: 'no-border fix-indent',
    expandCollapse: false,
    repeatBlock: {
      id: 'treatySpecialRatesClaim',
      title: 'questionnaire.treatySpecialRatesClaim',
      component: QuestionTreatySpecialRatesClaimBlock,
      componentProps: {
        optional: true
      },
      info: {
        instructions: 'questionnaire.treatySpecialRatesClaim.info'
      },
    },
    minSize: 0,
    maxSize: 1,
    addText: 'questionnaire.treatySpecialRatesClaim.addLabel',
    visible: bAnswers => bAnswers?.country,
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);
  const isSpecialRatesOpen = () => {
    if (!isEmptyObject(treatySpecialRatesClaims?.answer)) {
      return true;
    }

    const answers = treatySpecialRatesClaims?.answer ? Object.values(treatySpecialRatesClaims?.answer) : [];
    if (answers.length > 0) {
      return true;
    }

    return false;
  };

  const getRequiredValidator = errorMessage => ({ validation: {
    conditions: [
      { condition: requiredValidator, message: errorMessage }
    ]
  } });

  const isBlockValid = () => {
    let forceValidation = false;
    if (typeof block.linkBlock === 'boolean') {
      forceValidation = block.linkBlock !== true;
    }
    if (typeof block.linkBlock === 'function') {
      forceValidation = block.linkBlock(block.answers, qAnswers) === false;
    }

    if (!forceValidation && (!answer || isEmptyObject(answer))) {
      return true;
    }

    if (block.componentProps?.optional === true && !forceValidation) {
      if (!claimed?.answer) {
        return true;
      }
    }

    if (forceValidation) {
      const newClaimedValidation = getRequiredValidator('questionnaire.treatyClaim.claimed.errorMessage');
      const newClaimed = { ...claimed, ...newClaimedValidation };
      setClaimed(newClaimed);
    }

    const questions = [claimed, country, limitationOnBenefit, other];
    const blockIsValid = isBlockFullyValid(null, questions, block?.answers, qAnswers);

    const isTreatySpecialRatesClaimValid = isSpecialRatesOpen()
      ? treatySpecialRatesClaims.isValid
      : true;

    return (isTreatySpecialRatesClaimValid && blockIsValid);
  };

  const updateDependentQuestion = (existingQuestion, newAnswer, setState, required, items, errorMessage) => {
    const newValidation = required
      ? getRequiredValidator(errorMessage)
      : { validation: { conditions: undefined } };

    const newQuestion = {
      ...existingQuestion,
      answer: newAnswer,
      isValid: undefined,
      errorMessages: undefined,
      componentProps: { ...existingQuestion.componentProps, items },
      ...newValidation,
    };

    if (existingQuestion.id === 'other' && required === true) {
      newQuestion.className = 'col-6';
    } else if (existingQuestion.id === 'limitationOnBenefit' && newAnswer === 'Other') {
      newQuestion.className = 'col-6';
    } else {
      newQuestion.className = 'col-12';
    }

    validateQuestion(newQuestion);
    setState(newQuestion);

    return newQuestion;
  };

  const updateLimitationOnBenefit = (countryAnswer) => {
    const items = getLobItems(countryAnswer);
    let newAnswer;
    if (!claimed || !countryAnswer || !items) {
      newAnswer = undefined;
    } else if (items?.length === 1 && items[0].value === '') {
      newAnswer = '';
    } else if (items?.length === 2 && items[0].value === '') {
      newAnswer = items[1].value;
    } else if (items?.find(item => item.value === limitationOnBenefit?.answer)) {
      newAnswer = limitationOnBenefit?.answer;
    }

    if ((items.length === 1 || items.length === 2) && items[0].value === '') {
      limitationOnBenefit.disabled = true;
    } else {
      limitationOnBenefit.disabled = false;
    }

    const newLimitationOnBenefit = updateDependentQuestion(
      limitationOnBenefit,
      newAnswer,
      setLimitationOnBenefit,
      countryAnswer,
      items,
      'questionnaire.taxTreatyClaim.limitationOnBenefit.errorMessage'
    );

    return newLimitationOnBenefit;
  };

  const updateOther = (countryAnswer, limitationOnBenefitAnswer) => {
    const items = getOthers(countryAnswer);
    let newAnswer;
    const hasSingleSelectableValue = items.length === 2 && items[0].value === '';
    if (hasSingleSelectableValue) {
      newAnswer = items[1].value;
    }

    const required = limitationOnBenefitAnswer === 'Other';
    const newOther = updateDependentQuestion(
      other,
      newAnswer,
      setOther,
      required,
      items,
      'questionnaire.taxTreatyClaim.other.errorMessage'
    );

    return newOther;
  };

  const onTreatySpecialRatesClaimAnswer = (b, a, v) => {
    const newTreatySpecialRatesClaim = { ...b, answer: a, isValid: v };
    setTreatySpecialRatesClaims(newTreatySpecialRatesClaim);
    setAnswer({ ...answer, treatySpecialRatesClaims: a });
  };

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'claimed': {
        const newClaimed = { ...q, answer: value };
        validateQuestion(newClaimed);
        setClaimed(newClaimed);
        const countryValidation = value === 'Yes' ? getRequiredValidator('questionnaire.treatyClaim.country.errorMessage') : undefined;
        const countryChanges = { answer: undefined, isValid: undefined, errorMessages: undefined };
        const newCountry = { ...country, ...countryValidation, ...countryChanges };
        validateQuestion(newCountry);
        setCountry(newCountry);
        setAnswer(value === 'Yes' ? { ...answer, claimed: value } : { claimed: value });
        break;
      }
      case 'country': {
        if (value) {
          dispatch(getSpecialRateProvisions(value, answer?.limitationOnBenefit === 'TaxExempt'));
        } else {
          dispatch(clearSpecialRateProvisions());
        }
        const newCountryValidation = value || block.linkBlock(null, qAnswers) === false
          ? getRequiredValidator('questionnaire.treatyClaim.country.errorMessage')
          : { validation: { conditions: undefined } };
        const newCountry = { ...q, answer: value, ...newCountryValidation };
        validateQuestion(newCountry);
        setCountry(newCountry);
        const newLimitationOnBenefit = updateLimitationOnBenefit(value);
        const newOther = updateOther(value, newLimitationOnBenefit?.answer);
        setAnswer({
          ...answer,
          country: value,
          limitationOnBenefit: newLimitationOnBenefit?.answer ?? undefined,
          other: newOther?.answer ?? undefined });
        break;
      }
      case 'limitationOnBenefit': {
        if (limitationOnBenefit?.answer !== 'TaxExempt' && value === 'TaxExempt') {
          dispatch(getSpecialRateProvisions(answer?.country, true));
        } else if (limitationOnBenefit?.answer === 'TaxExempt' && value !== 'TaxExempt') {
          dispatch(getSpecialRateProvisions(answer?.country, false));
        }
        const newLimitationOnBenefit = {
          ...q,
          visible: bAnswers => entityType === 'Entity' && bAnswers?.country,
          answer: value
        };
        let newOther;
        if (value === 'Other') {
          newLimitationOnBenefit.className = 'col-6';
          newOther = updateOther(country?.answer, value);
        } else {
          newLimitationOnBenefit.className = 'col-12';
        }

        validateQuestion(newLimitationOnBenefit);
        setLimitationOnBenefit(newLimitationOnBenefit);
        setAnswer({ ...answer,
          limitationOnBenefit: value,
          other: newOther?.answer ?? undefined });
        break;
      }
      case 'other': {
        const newOther = {
          ...q,
          visible: bAnswers => bAnswers?.limitationOnBenefit === 'Other',
          answer: value
        };
        validateQuestion(newOther);
        setOther(newOther);
        setAnswer({ ...answer, other: value });
        break;
      }
      case 'isUsSourceDividends': {
        const newIsUsSourceDividends = { ...q, answer: value };
        validateQuestion(newIsUsSourceDividends);
        setIsUsSourceDividends(newIsUsSourceDividends);
        setAnswer({ ...answer, isUsSourceDividends: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.linkBlock(null, qAnswers) === false) {
      const newCountryValidation = getRequiredValidator('questionnaire.taxTreatyClaim.country.errorMessage');
      const newCountry = { ...country, ...newCountryValidation };
      setCountry(newCountry);
      setAnswer({ ...answer, country: undefined });
    }
  }, [block.linkBlock(null, qAnswers)]);

  useEffect(() => {
    dispatch(getOtherArticles());
  }, []);

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isBlockValid()));
    } else {
      onAnswer(block, answer, isBlockValid());
    }
  }, [answer, qAnswers?.entityType]);

  useEffect(() => {
    if (claimed.answer !== block.answers?.claimed) {
      onQuestionChange(claimed, block.answers?.claimed);
    }
    if (country.answer !== block.answers?.country) {
      onQuestionChange(country, block.answers?.country);
    }
    if (limitationOnBenefit.answer !== block.answers?.limitationOnBenefit) {
      onQuestionChange(limitationOnBenefit, block.answers?.limitationOnBenefit);
    }
    if (other.answer !== block.answers?.other) {
      onQuestionChange(other, block.answers?.other);
    }
    if (isUsSourceDividends.answer !== block.answers?.isUsSourceDividends) {
      onQuestionChange(isUsSourceDividends, block.answers?.isUsSourceDividends);
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.claimed) {
        onQuestionChange({ ...claimed, isForceEdited: true }, '');
      }
      if (answer?.claimed === 'Yes' && !answer?.country) {
        onQuestionChange({ ...country, isForceEdited: true }, '');
      }
      if (answer?.claimed === 'Yes' && answer?.country !== undefined && answer?.treatySpecialRatesClaims === undefined) {
        onQuestionChange({ ...treatySpecialRatesClaims, isForceEdited: true }, '');
      }
      if (!answer?.country) {
        onQuestionChange({ ...country, isForceEdited: true }, '');
      }
      if (!answer?.limitationOnBenefit) {
        onQuestionChange({ ...limitationOnBenefit, isForceEdited: true }, '');
      }
      if (!answer?.other) {
        onQuestionChange({ ...other, isForceEdited: true }, '');
      }
      if (!answer?.isUsSourceDividends) {
        onQuestionChange({ ...isUsSourceDividends, isForceEdited: true }, '');
      }
      if (answer?.treatySpecialRatesClaims) {
        const newBlock = { ...treatySpecialRatesClaims, isForceEdited: true };
        setTreatySpecialRatesClaims(newBlock);
      }
    }

    if (block.isForceEdited === undefined) {
      if (claimed.isForceEdited === true) {
        onQuestionChange({ ...claimed, isForceEdited: undefined });
      }
      if (country.isForceEdited === true) {
        onQuestionChange({ ...country, isForceEdited: undefined });
      }
      if (limitationOnBenefit.isForceEdited === true) {
        onQuestionChange({ ...limitationOnBenefit, isForceEdited: undefined });
      }
      if (other.isForceEdited === true) {
        onQuestionChange({ ...other, isForceEdited: undefined });
      }
      if (isUsSourceDividends.isForceEdited === true) {
        onQuestionChange({ ...isUsSourceDividends, isForceEdited: undefined });
      }
      if (treatySpecialRatesClaims.isForceEdited === true) {
        const newBlock = { ...treatySpecialRatesClaims, isForceEdited: undefined };
        setTreatySpecialRatesClaims(newBlock);
      }
    }
  }, [block.isForceEdited]);

  const isSpecialRatesClaimVisible = useMemo(() => {
    if (limitationOnBenefit?.answer === 'Other') {
      return claimed?.answer === 'Yes' && country?.answer && other?.answer;
    }

    if (entityType === 'Individual') {
      return claimed?.answer === 'Yes' && country?.answer;
    }

    return claimed?.answer === 'Yes' && country?.answer && limitationOnBenefit?.answer;
  }, [claimed?.answer, country?.answer, limitationOnBenefit?.answer, other?.answer]);

  return (
    <div className="container">
      <div className="row">
        <Question key={ claimed.id } blockId={ blockIdentifier } question={ claimed } { ...claimed.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
        { showQuestion(country, block?.answers, qAnswers)
        && <Question key={ country.id } blockId={ blockIdentifier } question={ country } { ...country.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
        { showQuestion(limitationOnBenefit, block?.answers, qAnswers)
        && <Question key={ limitationOnBenefit.id } blockId={ blockIdentifier } question={ limitationOnBenefit } { ...limitationOnBenefit.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
        { showQuestion(other, block?.answers, qAnswers)
        && <Question key={ other.id } blockId={ blockIdentifier } question={ other } { ...other.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
        { showQuestion(isUsSourceDividends, block?.answers, qAnswers)
      && <Question key={ isUsSourceDividends.id } blockId={ blockIdentifier } question={ isUsSourceDividends } { ...isUsSourceDividends.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
        { isSpecialRatesClaimVisible
        && <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ blockIdentifier }-${ treatySpecialRatesClaims.id }` } block={ treatySpecialRatesClaims } onAnswer={ onTreatySpecialRatesClaimAnswer } />}
      </div>
    </div>);
};

QuestionTaxTreatyClaimBlock.displayName = 'QuestionTaxTreatyClaimBlock';

QuestionTaxTreatyClaimBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape()
};

QuestionTaxTreatyClaimBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionTaxTreatyClaimBlock;
